/**
 * Soft-safe Move 3: Cards (holes) step panel.
 * Soft-safe ③ / UX 4: holes layer precedes board in share order (owned by Summary).
 */
import {
  t,
} from '../../i18n/strings'
import {
  formatSeatCode,
  getPositions,
  usedCards,
} from '../../lib/poker'
import type {
  PickTarget,
} from '../../lib/types'
import {
  assignCard,
  clearSlot,
  getHolePairForSeat,
  getState,
  holeSeatFromTarget,
  isHoleTarget,
  makeSeatHoleTarget,
  resolvePickTargetForAssign,
  setCardsSeat,
  setHolesMode,
  setPickTarget,
} from '../../state'
import {
  cardGrid,
  playingCard,
} from '../cards'
import {
  el,
} from '../dom'
import {
  renderTableViz,
} from '../tableViz'
import {
  explainOnly,
  playInfoFromHand,
  requestStep,
} from './helpers'


export function slotClick(target: PickTarget, hasCard: boolean): void {
  if (hasCard) clearSlot(target)
  else setPickTarget(target)
}

export function renderCards(): HTMLElement {
  const ui = t()
  const { hand, pickTarget, step, holesMode } = getState()
  const used = usedCards(hand)
  const target = resolvePickTargetForAssign(step, hand, pickTarget)
  const pickerOk = target != null && isHoleTarget(target)
  const activeSeat = holeSeatFromTarget(pickTarget) ?? 'hero'
  const positions = getPositions(hand.playerCount)
  const villainSeats = positions.filter((p) => p !== hand.heroPosition)
  const pair = getHolePairForSeat(hand, activeSeat)
  const isHeroSeat = activeSeat === 'hero'
  const empty0 = isHeroSeat ? ui.cardSlot1 : ui.cardSlotSelect
  const empty1 = isHeroSeat ? ui.cardSlot2 : ui.cardSlotSelect
  const slot0: typeof pickTarget = isHeroSeat
    ? 'hole0'
    : makeSeatHoleTarget(activeSeat, 0)
  const slot1: typeof pickTarget = isHeroSeat
    ? 'hole1'
    : makeSeatHoleTarget(activeSeat, 1)

  const heroFilled = Boolean(hand.holeCards[0] && hand.holeCards[1])
  const showVillains = holesMode === 'withVillains'

  const modePills = el(
    'div',
    {
      className: 'holesModePills',
      attrs: { role: 'group', 'aria-label': ui.holesModeAria },
    },
    [
      el('button', {
        className: [
          'chip',
          'holesModeChip',
          holesMode === 'heroOnly' ? 'chipActive' : '',
        ]
          .filter(Boolean)
          .join(' '),
        attrs: {
          type: 'button',
          'aria-pressed': holesMode === 'heroOnly' ? 'true' : 'false',
        },
        text: ui.holesModeHeroOnly,
        on: { click: () => setHolesMode('heroOnly') },
      }),
      el('button', {
        className: [
          'chip',
          'holesModeChip',
          holesMode === 'withVillains' ? 'chipActive' : '',
        ]
          .filter(Boolean)
          .join(' '),
        attrs: {
          type: 'button',
          'aria-pressed': holesMode === 'withVillains' ? 'true' : 'false',
        },
        text: ui.holesModeWithVillains,
        on: { click: () => setHolesMode('withVillains') },
      }),
    ],
  )

  const seatChildren: (HTMLElement | null)[] = [
    el(
      'button',
      {
        className: [
          'chip',
          'seatChip',
          'seatChipHero',
          activeSeat === 'hero' ? 'chipActive' : '',
          heroFilled ? 'seatChipFilled' : '',
        ]
          .filter(Boolean)
          .join(' '),
        attrs: {
          type: 'button',
          'aria-pressed': activeSeat === 'hero' ? 'true' : 'false',
          'aria-label': ui.heroChip(hand.heroPosition),
        },
        on: { click: () => setCardsSeat('hero') },
      },
      [
        el('span', {
          className: 'seatChipLabel',
          text: ui.heroChip(hand.heroPosition),
        }),
        heroFilled
          ? el('span', {
              className: 'seatChipBadge',
              text: ui.seatFilled,
              attrs: { 'aria-hidden': 'true' },
            })
          : null,
      ],
    ),
  ]
  if (showVillains) {
    for (const pos of villainSeats) {
      const known = hand.knownHoles?.[pos]
      const hasAny = Boolean(known?.[0] || known?.[1])
      const filledBoth = Boolean(known?.[0] && known?.[1])
      const label = ui.seatVillain(pos)
      seatChildren.push(
        el(
          'button',
          {
            className: [
              'chip',
              'seatChip',
              'seatChipVillain',
              activeSeat === pos ? 'chipActive' : '',
              hasAny ? 'seatChipFilled' : '',
            ]
              .filter(Boolean)
              .join(' '),
            attrs: {
              type: 'button',
              'aria-pressed': activeSeat === pos ? 'true' : 'false',
              'aria-label': label,
            },
            on: { click: () => setCardsSeat(pos) },
          },
          [
            el('span', { className: 'seatChipLabel', text: label }),
            filledBoth || hasAny
              ? el('span', {
                  className: 'seatChipBadge',
                  text: ui.seatFilled,
                  attrs: { 'aria-hidden': 'true' },
                })
              : null,
          ],
        ),
      )
    }
  }

  const seatChips = el(
    'div',
    {
      className: ['seatChips', showVillains ? '' : 'seatChipsHeroOnly']
        .filter(Boolean)
        .join(' '),
      attrs: { role: 'group', 'aria-label': ui.seatsLabel },
    },
    seatChildren,
  )

  // Softwise M10e: no hero-done hint copy (chip is seat name only).
  const heroDoneBanner = heroFilled
    ? el('div', { className: 'heroCardsDoneBanner', attrs: { role: 'status' } }, [
        el('div', { className: 'heroCardsDoneActions' }, [
          // Softwise M10b: Next now goes straight to the Line (no board step),
          // so this duplicate jump is Quiet — one filled button per screen.
          el('button', {
            className: 'btn btnQuiet heroCardsDoneCta',
            attrs: { type: 'button' },
            text: ui.heroCardsDoneCta,
            on: { click: () => requestStep('actions') },
          }),
          !showVillains
            ? el('button', {
                className: 'btn heroCardsAddVillainsCta',
                attrs: { type: 'button' },
                text: ui.heroCardsAddVillainsCta,
                on: { click: () => setHolesMode('withVillains') },
              })
            : null,
        ]),
      ])
    : null

  // Softwise M10d: the guide stays until both hero holes are in, so the
  // picker never jumps up after the first card (no mid-input layout shift).
  const heroHolesPending = !heroFilled

  return el('section', { className: 'panel' }, [
    el('h2', { className: 'panelTitle', attrs: { tabindex: -1 }, text: ui.cardsTitle }),
    explainOnly(el('p', { className: 'panelHint', text: ui.cardsHint })),
    heroHolesPending
      ? el('p', {
          className: 'muted heroHolesGuide',
          // Soft F: seat name (BTN etc.), never `Hero`.
          text: ui.heroHolesGuide(formatSeatCode(hand.heroPosition, hand.playerCount)),
          attrs: { role: 'status' },
        })
      : null,
    // Softwise M7: while picking hero holes, picker is Soft L1 — Quiet mode pills.
    heroFilled ? modePills : null,
    el('div', { className: 'tablePreviewWrap' }, [
      renderTableViz({
        playerCount: hand.playerCount,
        heroPosition: hand.heroPosition,
        mode: showVillains ? 'holePick' : 'preview',
        compact: true,
        activeSeat,
        heroFilled,
        filledVillainSeats: new Set(
          villainSeats.filter((pos) => {
            const known = hand.knownHoles?.[pos]
            return Boolean(known?.[0] || known?.[1])
          }),
        ),
        playInfo: playInfoFromHand(hand),
        onSelectSeat: showVillains
          ? (pos) => {
              if (pos === hand.heroPosition) setCardsSeat('hero')
              else setCardsSeat(pos)
            }
          : undefined,
      }),
    ]),
    // Softwise M7: seat chips only when adding villains — picker stays Soft L1.
    showVillains
      ? el('div', { className: 'seatsSectionHeader', text: ui.holesBySeat })
      : null,
    showVillains ? seatChips : null,
    showVillains
      ? el('p', {
          className: 'softHint knownHolesHint',
          text: ui.knownHolesOptional,
        })
      : null,
    el('div', { className: 'selectedRow' }, [
      playingCard({
        card: pair[0],
        large: true,
        emptyLabel: empty0,
        selected: pickTarget === slot0,
        onClick: () => slotClick(slot0, Boolean(pair[0])),
      }),
      playingCard({
        card: pair[1],
        large: true,
        emptyLabel: empty1,
        selected: pickTarget === slot1,
        onClick: () => slotClick(slot1, Boolean(pair[1])),
      }),
    ]),
    pickerOk
      ? cardGrid({ used, onPick: (c) => assignCard(c, used) })
      : el('p', { className: 'softHint', text: ui.pickerDisabled }),
    heroDoneBanner,
  ])
}
